<template>
  <div>
    <div
      v-for="(row, index) in localValue"
      :key="index"
    >
      <v-row class="ma-0" :data-dynamic-row="true">
        <v-col cols="11" class="pa-0">
          <v-row class="ma-0">
            <v-col
              v-for="field in fields"
              :key="field.value"
              :cols="`${field.col || 12}`"
              class="pa-0 pr-2"
            >
              <component
                v-if="field.isCustom"
                :is="field.component"
                :key="`custom-${field.value}-${index}`"
                v-model="rowModels[index].value"
                v-bind="getCustomProps(field, row, index)"
                @validation="(valid) => updateRowValidation(index, valid)"
              />
              <component
                v-else
                :is="field.component"
                v-model="row[field.value]"
                v-bind="getProps(field, row)"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="1" class="pa-0">
          <v-btn icon size="small" class="elevation-0" @click="removeRow(index)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn
            v-if="index + 1 === localValue.length"
            icon
            size="small"
            class="elevation-0"
            @click="addRow"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-divider v-if="summary?.show" class="mb-4" />

    <v-row v-if="summary?.show">
      <v-col cols="12" class="text-end font-weight-bold">
        Total: {{ formatCurrency(getTotal) }}
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import type { PropType } from 'vue';
import type { FormField } from '@/types/form';

const props = defineProps({
  modelValue: Array as PropType<any[]>,
  fields: Array as PropType<FormField[]>,
  summary: Object as PropType<{
    show: boolean;
    fields?: string[];
    formatter?: (items: Record<string, any>[]) => Record<string, any>;
  }>,
  form: { type: Object as PropType<Record<string, any>>, required: true }
});
const emit = defineEmits(['update:modelValue', 'validation']);

const localValue = ref<any[]>([]);
const rowValidations = ref<boolean[]>([]);

// 🔁 Para evitar loops infinitos al sincronizar
let skipNextSync = false;

// 🔄 Sincroniza cada vez que cambia el modelValue desde afuera (ej. nuevo item)
watch(
  () => props.modelValue,
  (newVal) => {
    skipNextSync = true;

    if (Array.isArray(newVal) && newVal.length) {
      localValue.value = newVal.map(row => ({ ...row }));
      rowValidations.value = newVal.map(() => true);
    } else {
      localValue.value = [{}];
      rowValidations.value = [false];
    }

    nextTick(() => {
      skipNextSync = false;
    });
  },
  { immediate: true, deep: true }
);

// 📤 Emite cambios solo cuando son interactivos (no programáticos)
watch(
  () => localValue.value.map(row => ({ ...row })),
  (val) => {
    if (!skipNextSync) {
      emit('update:modelValue', val);
    }
  },
  { deep: true }
);

// ✅ Valida internamente
watch(rowValidations, () => {
  emit('validation', rowValidations.value.every(Boolean));
}, { deep: true });

const rowModels = computed(() =>
  localValue.value.map((row) => ({
    get value() {
      return row;
    },
    set value(val) {
      Object.assign(row, val);
    }
  }))
);

const addRow = async () => {
  const row: Record<string, any> = {};
  props.fields?.forEach(f => (row[f.value] = null));
  localValue.value.push(row);
  rowValidations.value.push(false);

  await nextTick();

  // Enfoca el primer input del nuevo renglón
  const lastRow = document.querySelectorAll('[data-dynamic-row]')?.[localValue.value.length - 1];
  const input = lastRow?.querySelector('input, textarea, select');
  if (input) (input as HTMLElement).focus();
};

const removeRow = (index: number) => {
  localValue.value.splice(index, 1);
  rowValidations.value.splice(index, 1);
  if (localValue.value.length === 0) addRow();
};

const updateRowValidation = (index: number, valid: boolean) => {
  rowValidations.value[index] = valid;
};

const getProps = (field: FormField, row: any) => {
  const { children, component, value, ...rest } = field;
  const props: Record<string, any> = {
    ...rest,
    label: field.label,
    type: field.type || 'text',
    clearable: false,
    density: 'compact',
    rules: field.required ? [(v: any) => !!v || 'Requerido'] : [],
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    'prepend-inner-icon': field.icon,
  };

  if (field.options) props.items = field.options;

  return props;
};

const formatCurrency = (val: number) => `$${val.toFixed(2)}`;

const getTotal = computed(() => {
  return props.summary?.formatter ? props.summary.formatter(localValue.value).total || 0 : 0;
});

const getCustomProps = (field: FormField, row: any, index: number) => {
  return typeof field.props === 'function' ? field.props(row, props.form) : {};
};
</script>
